'use client';

import {
  matchesPublicCategoryProductsSeed,
  type PublicCategoryProductsSeed,
} from '@eliadmin/core/src/components/categories/public-category-products-seed';
import { buildProductsRequest } from '@eliadmin/core/src/api/products/product-list-request';
import {
  categoryProductsQueryOptions,
  normalizeCategoryPage,
} from '@eliadmin/core/src/components/categories/category-query-state';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import type { Category } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';
import CategorySortToggle from '@eliadmin/core/src/components/categories/category-sort-toggle';
import { ProductsGrid } from '@eliadmin/core/src/components/product/products-grid';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Pagination } from '@eliadmin/core/src/components/ui/pagination';
import { Skeleton } from '@eliadmin/core/src/components/ui/skeleton';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalNavigation, useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { buildProductsQuery } from '@eliadmin/core/src/utils/product-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import { Paragraph } from '../ui/paragraph';
import { CategoryFilterSheet } from './category-filter-sheet';
import { CollectionPageSchema } from '@eliadmin/core/src/components/seo/product-list-schema';

export type CategoryProps = {
  initialProducts?: PublicCategoryProductsSeed;
  className?: {
    grid_wrapper?: string;
    product?: string;
  };
  category: Category;
  onProductTotalChange?: (total: number) => void;
  paginationProps?: { scrollToElementAfterPageChange?: string };
};

export function CategoryProducts(props: CategoryProps) {
  const locale = useLocaleStore(s => s.currentLocale);
  const authToken = useCustomerStore(s => s.me?.authToken);
  const isCustomerInitialized = useCustomerStore(s => s.isInitialized);
  const filters = useCategoryStore(s => s.filter);
  const paramsReady = useCategoryStore(s => s.paramsReady);
  const storedPage = useCategoryStore(s => s.page);
  const setPage = useCategoryStore(s => s.setPage);
  const searchParams = useSearchParams();
  const queryClient = useQueryClient();

  const currentPage = normalizeCategoryPage(storedPage ?? searchParams.get('page'));

  const { t } = useTranslations();

  const generalLayout = useGlobalStore(s => s.generalLayout);

  const queryWithoutPage = buildProductsQuery(
    categoryProductsQueryOptions(
      props.category.id,
      generalLayout?.pagination?.paginationProducts || 12,
      filters,
    ),
  );

  const productRequestUri = buildProductsRequest({
    ...queryWithoutPage,
    page: currentPage,
  }).uri;

  const productQueryKey = [
    CacheKey.PRODUCTS,
    'infinite',
    currentPage,
    productRequestUri,
    authToken,
    locale,
    filters.attributes,
  ];

  const hasSeed = matchesPublicCategoryProductsSeed(props.initialProducts, {
    locale,
    requestUri: productRequestUri,
    authToken,
    page: currentPage,
  });
  // Do not toggle an aged public seed from disabled to enabled after /me.
  const queryReady = hasSeed || (isCustomerInitialized && paramsReady !== false);
  const productsQuery = useInfiniteQuery({
    initialData: hasSeed
      ? { pages: [props.initialProducts!.data], pageParams: [currentPage] }
      : undefined,
    initialDataUpdatedAt: props.initialProducts?.updatedAt,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
    queryKey: productQueryKey,
    queryFn: ({ pageParam }) => {
      return fetchProducts({
        query: {
          ...queryWithoutPage,
          page: pageParam,
        },
        locale,
        authToken,
      });
    },
    getNextPageParam: lastPage => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: currentPage,
    enabled: queryReady,
  });

  const isGlobalNavigating = useGlobalNavigation();

  const lastPage = productsQuery.data?.pages[productsQuery.data?.pages.length - 1];
  const flatList = productsQuery.data?.pages.flatMap(page => page.data) ?? [];

  // Kým sa nedokončí inicializácia zákazníka a prvé načítanie produktov, je flatList
  // prázdny - bez tohto gate-u by sa medzitým zobrazila hláška "žiadny tovar nebol nájdený".
  const isLoadingProducts =
    (!isCustomerInitialized && !productsQuery.data) || productsQuery.isPending;
  const isEmpty = !isLoadingProducts && !productsQuery.isError && !flatList.length;

  useEffect(() => {
    if (typeof lastPage?.meta?.total === 'number') {
      props.onProductTotalChange?.(lastPage.meta.total);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastPage?.meta?.total]);

  return (
    <div
      className={cn(
        'relative flex w-full flex-col items-center justify-center gap-6',
        'ea-category-products',
      )}
    >
      <div className="flex w-full flex-col items-center justify-center gap-6 sm:flex-row sm:flex-wrap sm:justify-between">
        <CategorySortToggle />
        <CategoryFilterSheet />
      </div>
      {!!isLoadingProducts && <CategoryProductsSkeleton className={{ container: 'w-full' }} />}

      {!!productsQuery.isError && (
        <div className="flex flex-col items-center gap-4">
          <p className="text-center text-muted-foreground">Produkty sa nepodarilo načítať.</p>
          <Button
            className="uppercase"
            isLoading={productsQuery.isFetching}
            onClick={() => {
              if (queryReady) void productsQuery.refetch();
            }}
          >
            skúsiť znova
          </Button>
        </div>
      )}

      {!!isEmpty && <p className="text-center text-muted-foreground">{t('text554')}</p>}

      {!isLoadingProducts && !!flatList.length && (
        <>
          <CollectionPageSchema
            category={props.category}
            products={flatList}
            order={filters.sort.order ?? 'desc'}
            url={props.category.breadcrumb?.slug || props.category.seo?.['og:url']}
            currency={(generalLayout?.currency_code || generalLayout?.currency)}
          />
          <ProductsGrid
            products={flatList}
            emitSchema={false}
            className={{
              wrapper: cn(
                'ea-category-products__grid',
                'pr-4 sm:pr-0',
                props.className?.grid_wrapper,
              ),
              product: {
                main: cn(props.className?.product),
              },
            }}
          />
        </>
      )}

      {!isLoadingProducts && !!productsQuery.hasNextPage && (
        <div
          className={cn(
            'flex flex-row justify-center pr-4 sm:pr-0',
            'ea-category-products__load-more',
          )}
        >
          <Button
            isLoading={productsQuery.isFetchingNextPage}
            className="uppercase"
            onClick={() => {
              if (queryReady) void productsQuery.fetchNextPage();
            }}
          >
            zobraziť viac
          </Button>
        </div>
      )}

      <div
        className={cn(
          'flex flex-row justify-center pr-4 sm:pr-0',
          'ea-category-products__pagination',
        )}
      >
        <Pagination
          shouldReplace
          setPage={setPage}
          links={lastPage?.meta.links}
          onPageClicked={page => {
            if (String(page) !== String(currentPage)) return;
            queryClient.resetQueries({
              queryKey: productQueryKey,
              exact: true,
            });
          }}
          scrollToElementAfterPageChange={
            props.paginationProps?.scrollToElementAfterPageChange || undefined
          }
        />
      </div>

      {!!isGlobalNavigating && (
        <div
          className={cn(
            'absolute inset-0 z-10 bg-background/30',
            'ea-category-products__loading-overlay',
          )}
        ></div>
      )}
    </div>
  );
}

type CategoryProductsSkeletonProps = {
  className?: {
    container?: string;
    items_wrapper?: string;
    item?: string;
  };
  numberOfSkeletons?: number;
};

export function CategoryProductsSkeleton({
  className,
  numberOfSkeletons,
}: CategoryProductsSkeletonProps) {
  const templateSettings = useGlobalStore(s => s.templateSettings);

  const array = Array.from({ length: numberOfSkeletons || 6 }, (_, i) => i);

  return (
    <div
      className={cn(
        'relative col-span-12 flex w-full flex-col gap-4 @container',
        'ea-category-products',
        {
          'col-span-12': !templateSettings.category?.left_filter?.enabled,
        },
        className?.container,
      )}
    >
      <div
        className={cn(
          'grid w-full gap-4 @sm:grid-cols-1 @lg:grid-cols-2 @2xl:grid-cols-3',
          className?.items_wrapper,
        )}
      >
        {array.map(i => (
          <Skeleton key={i} className={cn('min-h-[400px] min-w-[250px]', className?.item)} />
        ))}
      </div>

      <Skeleton className="h-20 w-full" />
    </div>
  );
}
